import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import { Autocomplete } from '@material-ui/lab'
import { Box, Button, Collapse, Grid, TextField } from '@material-ui/core'

import { SectionCard } from 'components'
import { DynamicField } from './components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'
import useAssetInventory from 'hooks/useAssetInventory'

import theme from 'theme'
import * as service from 'service'
import constants from 'constants/index'
import moment from 'moment'

const AssetInventoryForm = forwardRef(
  ({ setLoading = () => {}, mode = 'new', assetInventory = {} }, ref) => {
    const { DATE_FIELD_TYPE } = constants.assetInventory
    const { MANAGE_STEP_2, MANAGE_STEP_3 } = constants.tours.assetInventory

    const [defaultValues, setDefaultValues] = useState({
      name: assetInventory?.name || '',
      description: assetInventory?.description || '',
      assetInventoryCategory: null,
      categoryFields: [],
    })

    const history = useHistory()
    const snackbar = useSnackbar()
    const { assetInventoryCategories, isLoading: loadingCategories } =
      useAssetInventory()

    // Os campos dinâmicos do formulário são definidos após a escolha da categoria.
    // Como a leitura do schema ocorre apenas na inicialização do componente,
    // esses campos adicionais não são contemplados pelo schema de validação.
    // Por isso, as regras de validação para os campos dinâmicos são aplicadas diretamente
    // nos rules do React Hook Form, garantindo que cada campo tenha suas restrições corretamente.
    const formMethods = useForm({ defaultValues })

    const { control, errors, handleSubmit, reset, watch, setValue } =
      formMethods

    const assetInventoryCategory = watch('assetInventoryCategory')

    const selectedCategory = assetInventoryCategories?.find(
      (category) => category.id === assetInventoryCategory?.id,
    )

    const selectedCategoryFields =
      selectedCategory?.assetInventoryCategoryFields || []

    const isShow = mode === 'show'
    const isNew = mode === 'new'

    const handleBackRedirection = () => history.goBack()

    const buildAssetInventoryPayload = (data) => {
      const { assetInventoryCategory, categoryFields, ...formData } = data

      const assetInventoryFieldAnswers = categoryFields?.map((item, idx) => {
        const field = selectedCategoryFields[idx]
        const isDateType = field?.fieldType === DATE_FIELD_TYPE

        return {
          id: field?.id,
          answer:
            isDateType && item.answer
              ? moment(item.answer).format('YYYY-MM-DD')
              : item.answer,
        }
      })

      return {
        ...formData,
        assetInventoryCategoryId: assetInventoryCategory?.id,
        assetInventoryFieldAnswers,
      }
    }

    const onSubmit = async (data) => {
      try {
        setLoading(true)

        const payload = buildAssetInventoryPayload(data)

        if (isNew) await service.dponet.assetInventory.create({ ...payload })
        else {
          await service.dponet.assetInventory.update({
            assetInventoryId: assetInventory.id,
            ...payload,
          })
        }

        snackbar.open({
          message: `Inventário de ativos ${
            isNew ? 'criado' : 'atualizado'
          } com sucesso!`,
          variant: 'success',
        })

        reset()
        handleBackRedirection()
      } catch (error) {
        console.error(error)
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error,
          ),
          variant: 'error',
        })
      } finally {
        setLoading(false)
      }
    }

    // Define os valores padrões do formulário, devido ao uso do find e múltipla
    // request os mesmo não consegue encontrar mesmo com validação de renderização.
    useEffect(() => {
      if (isEmpty(assetInventory)) return

      const defaultCategory = assetInventoryCategories?.find(
        (category) =>
          category.id === assetInventory?.assetInventoryCategory?.id,
      )

      const defaultCategoryFields =
        defaultCategory?.assetInventoryCategoryFields?.map((field) => {
          const fieldAnswer = assetInventory?.assetInventoryFieldAnswers?.find(
            (answer) => answer.assetInventoryCategoryField?.id === field.id,
          )

          return {
            id: field.id,
            answer: fieldAnswer?.answer ?? '',
          }
        })

      setDefaultValues({
        ...defaultValues,
        assetInventoryCategory: defaultCategory
          ? {
              id: defaultCategory.id,
              name: defaultCategory.name,
            }
          : null,
        categoryFields: defaultCategoryFields || [],
      })
    }, [assetInventory, assetInventoryCategories])

    useEffect(() => {
      reset(defaultValues)
    }, [defaultValues])

    // Tratativa para limpar os campos dinâmicos ao trocar a categoria
    useEffect(() => {
      if (selectedCategory?.id !== assetInventory?.assetInventoryCategory?.id) {
        const emptyCategoryFields = selectedCategoryFields.map((field) => ({
          id: field.id,
          answer: '',
        }))

        setValue('categoryFields', emptyCategoryFields)
      } else {
        setValue('categoryFields', defaultValues?.categoryFields || [])
      }
    }, [selectedCategory])

    useImperativeHandle(ref, () => ({
      hasSelectedCategoryFields: () => !isEmpty(selectedCategoryFields),
    }))

    return (
      <FormContext {...formMethods}>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
          component="form"
          id="asset-inventory-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <SectionCard title="Informações Gerais" id={MANAGE_STEP_2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Controller
                  control={control}
                  mode="onChange"
                  name="name"
                  rules={{
                    maxLength: {
                      value: 255,
                      message: 'Deve ter no máximo 255 caracteres',
                    },
                    required: 'Preencha esse campo',
                  }}
                  as={
                    <TextField
                      label="Nome"
                      error={!!errors?.name}
                      helperText={errors?.name?.message}
                      disabled={isShow}
                      type="text"
                      fullWidth
                      required
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  control={control}
                  mode="onChange"
                  name="assetInventoryCategory"
                  onChange={([, data]) => data || null}
                  rules={{
                    required: 'Preencha esse campo',
                  }}
                  as={
                    <Autocomplete
                      options={assetInventoryCategories || []}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      disabled={isShow}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Categoria"
                          error={!!errors.assetInventoryCategory}
                          helperText={
                            errors?.assetInventoryCategory?.message ||
                            errors?.assetInventoryCategory?.id?.message
                          }
                        />
                      )}
                      loading={loadingCategories}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  mode="onChange"
                  name="description"
                  rules={{
                    maxLength: {
                      value: 5000,
                      message: 'Deve ter no máximo 5000 caracteres',
                    },
                  }}
                  as={
                    <TextField
                      label="Descrição"
                      error={!!errors?.description}
                      helperText={errors?.description?.message}
                      placeholder="Descreva brevemente o inventário e sua finalidade"
                      multiline
                      minRows={4}
                      maxRows={10}
                      disabled={isShow}
                      fullWidth
                    />
                  }
                />
              </Grid>
            </Grid>
          </SectionCard>
          <Collapse in={!isEmpty(selectedCategoryFields)}>
            <SectionCard title="Informações adicionais" id={MANAGE_STEP_3}>
              <Grid container spacing={2}>
                {selectedCategoryFields.map((field, idx) => {
                  return (
                    <DynamicField
                      key={idx}
                      field={field}
                      isShow={isShow}
                      index={idx}
                    />
                  )
                })}
              </Grid>
            </SectionCard>
          </Collapse>
          <Box
            display="flex"
            justifyContent="flex-end"
            gridGap={theme.spacing(2)}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBackRedirection}
            >
              Voltar
            </Button>
            {!isShow && (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                form="asset-inventory-form"
              >
                Salvar
              </Button>
            )}
          </Box>
        </Box>
      </FormContext>
    )
  },
)

AssetInventoryForm.propTypes = {
  setLoading: PropTypes.func,
  mode: PropTypes.oneOf(['new', 'edit', 'show']).isRequired,
  assetInventory: PropTypes.object,
}

export default AssetInventoryForm
